// Phase 42 — toggleable call-setup timing instrumentation.
//
// OFF by default and a cheap no-op when off (every entry point short-circuits on
// a boolean read). The recon is code-certain on the accept→first-frame STRUCTURE
// but the actual milliseconds are runtime-only and unmeasurable on staging (the
// TURN stub short-circuits), so this exists to measure the real before/after on a
// live call.
//
// Enable EITHER of:
//   • localStorage.setItem('lx:callperf','1')   (persists across reloads)
//   • window.lxCallPerf(true)                    (runtime toggle, no reload)
// Then place / accept a call. When the first remote video frame renders, a
// console.table of stage timings (ms from the run's first mark) prints, plus an
// explicit "accept-click → first-remote-frame" delta. Read it in DevTools — there
// is no UI surface. window.lxCallPerf() returns the current on/off state.
//
// Stages marked across the stack: ring-prewarm / initiate / accept-click,
// media-acquired, respond-returned, turn-creds-returned, pc-created, offer-sent,
// answer-applied, ice-connected, media-armed, remote-track, first-remote-frame.
//
// ── 70 §11bis — TWO RUNS' MARKS ARE NOT COMPARABLE ACROSS ROLES ─────────────
// `t0` is THE FIRST MARK OF THE RUN (see callPerfMark below), and the first mark
// differs by role: `initiate` for the side that places the call, `ring-prewarm`
// for the side that is rung. Every later number is relative to a DIFFERENT
// event, so subtracting one run's stage from the other's measures nothing.
//
// This is not hypothetical. The 2026-08-11 capture reads `remote-track 234.2` on
// the initiating side and `remote-track 6.4` on the rung side, and the 6.4 looks
// impossible — no network round trip fits in 6.4 ms. It is not impossible and
// nothing is wrong with the mark:
//
//   • On the INITIATING side, t0 is `initiate`. Both collaborators in that
//     capture had the offerer on the far end (the IMPOLITE, lower-id peer is the
//     sole offerer — webrtcCall.ts's onnegotiationneeded), so `remote-track`
//     could only fire after the far end learned of the call, built its peer
//     connection and delivered its offer. 234.2 ms is that whole round trip.
//   • On the RUNG side, t0 is `ring-prewarm` — which happens when the ring
//     ARRIVES, i.e. after the caller has already written its offer to the
//     durable mailbox. The offer is sitting there before the clock starts. The
//     6.4 ms is the pre-warm's own catch-up `pull()` collecting a signal that
//     had ALREADY arrived, not a negotiation.
//
// So the two numbers measure different quantities from different zero points.
// The flush prints its zero point in the group header (`ms from ring-prewarm`)
// and that line is the whole warning — read it before comparing two runs.

let forced: boolean | null = null; // runtime override via window.lxCallPerf(true|false)

function enabled() {
  if (forced !== null) return forced;
  try {
    if (typeof window !== "undefined" && window.__lxCallPerf === true) return true;
    if (typeof localStorage !== "undefined" && localStorage.getItem("lx:callperf") === "1") return true;
  } catch { /* storage blocked — treat as off */ }
  return false;
}

function now() {
  try {
    return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  } catch {
    return Date.now();
  }
}

const runs = new Map<number, { t0: number; marks: [string, number][] }>(); // callId -> { t0, marks: [[stage, msFromT0]] }

// First mark for a callId establishes t0; later marks are relative to it.
export function callPerfMark(callId: number | string | null | undefined, stage: string) {
  if (!enabled() || callId == null) return;
  const id = Number(callId);
  const t = now();
  let r = runs.get(id);
  if (!r) {
    r = { t0: t, marks: [] };
    runs.set(id, r);
  }
  r.marks.push([stage, Math.round((t - r.t0) * 10) / 10]);
}

// Append a final stage (optional) and print the run's timeline, then clear it.
export function callPerfFlush(callId: number | string | null | undefined, stage?: string) {
  if (!enabled() || callId == null) return;
  const id = Number(callId);
  const r = runs.get(id);
  if (!r) return;
  if (stage) r.marks.push([stage, Math.round((now() - r.t0) * 10) / 10]);
  runs.delete(id);
  try {
    const acc = r.marks.find((m) => m[0] === "accept-click");
    const fin = r.marks[r.marks.length - 1];
    // eslint-disable-next-line no-console
    console.group(`[callperf] call ${id} — ${r.marks.length} stages (ms from ${r.marks[0] ? r.marks[0][0] : "start"})`);
    // eslint-disable-next-line no-console
    console.table(r.marks.map(([s, ms]) => ({ stage: s, ms })));
    if (acc && fin && fin[0] !== "accept-click") {
      // eslint-disable-next-line no-console
      console.log(`[callperf] accept-click → ${fin[0]}: ${Math.round((fin[1] - acc[1]) * 10) / 10} ms`);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  } catch { /* console shape differences — ignore */ }
}

export function callPerfEnable(on?: boolean) {
  forced = on === false ? false : on === true ? true : null;
  return enabled();
}

// Phase 42 diagnostic — expose the flag so the transport can gate temporary
// negotiation logging behind the same lx:callperf switch (OFF by default).
export function callDiagEnabled() {
  return enabled();
}

// Console convenience: window.lxCallPerf(true|false) toggles without a reload;
// window.lxCallPerf() reads the current state.
try {
  if (typeof window !== "undefined") {
    window.lxCallPerf = (v?: boolean) => (v === undefined ? enabled() : callPerfEnable(v));
  }
} catch { /* ignore */ }
